import { useEffect, useRef, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { Block, CreateTopBlockInput, MeResponse, Pillar } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { UserRefChip } from "../../components/UserRefChip";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { useRouter } from "../../router/Router";
import { BlockCard } from "./BlockCard";
import { pillarTint } from "./pillarColors";
import { SeasonPicker } from "./SeasonPicker";
import { useBlockSeason } from "./useBlockSeason";

interface State {
  status: "loading" | "ready" | "error";
  blocks: Block[];
  pillars: Pillar[];
  errorMessage: string | null;
}

const initial: State = { status: "loading", blocks: [], pillars: [], errorMessage: null };

const scores = [1, 2, 3, 4, 5];

// The Dashboard groups top-level blocks into their pillar sections — each
// headed by the pillar's executive sponsor — and headlines the four portfolio
// stats (req §9 / Appendix D). Restricted pillars and their blocks are filtered
// server-side, so a viewer without access simply sees no such section.
export function BlocksDashboard({ me }: { me: MeResponse }) {
  const router = useRouter();
  const [state, setState] = useState<State>(initial);
  const [payoutStartPct, setPayoutStartPct] = useState<number | null>(null);
  const { seasonId, season, seasons, setSeasonId, ready } = useBlockSeason(me);
  // A closed season's totals are frozen and the server rejects every create.
  const canCreate = hasPermission(me, "blocks:create_top") && season?.status !== "closed";
  const canConfigureEarning = hasPermission(me, "blocks:earning:configure");
  const canManagePillars = hasPermission(me, "blocks:pillars:manage");

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [pillarId, setPillarId] = useState("");
  const [team, setTeam] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [impact, setImpact] = useState(3);
  const [significance, setSignificance] = useState(3);
  const { keyFor, reset } = useIdempotencyKey();
  // Rapid season switches overlap, and only the latest may land: an older
  // season's projects would otherwise replace the selected one's.
  const latestRefresh = useRef(0);

  useEffect(() => {
    if (ready) {
      void refresh(seasonId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, ready]);

  // The payout-start threshold is a derived read-only headline (§6.4); a failure
  // to load it just hides the label rather than breaking the dashboard.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const curve = await apiClient.getEarningCurve();
        if (!cancelled) {
          setPayoutStartPct(curve.payout_start_pct);
        }
      } catch {
        // non-fatal — leave the label hidden
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function refresh(season: string) {
    const request = ++latestRefresh.current;
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));
    try {
      const [result, pillars] = await Promise.all([
        apiClient.listBlocks({ season_id: season, level: "top", limit: 100 }),
        apiClient.listPillars()
      ]);
      if (request === latestRefresh.current) {
        setState({ status: "ready", blocks: result.blocks, pillars, errorMessage: null });
      }
    } catch (caught) {
      if (request === latestRefresh.current) {
        setState({ status: "error", blocks: [], pillars: [], errorMessage: messageFor(caught) });
      }
    }
  }

  const activePillars = state.pillars.filter((pillar) => !pillar.archived);
  const effectivePillarId = pillarId !== "" ? pillarId : (activePillars[0]?.id ?? "");

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim() === "" || effectivePillarId === "" || dueDate === "" || creating) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    const payload: CreateTopBlockInput = {
      name: name.trim(),
      pillar_id: effectivePillarId,
      impact,
      significance,
      due_date: dueDate
    };
    if (seasonId !== "") {
      payload.season_id = seasonId;
    }
    if (team.trim() !== "") {
      payload.team = team.trim();
    }
    try {
      await apiClient.createTopBlock(payload, keyFor(JSON.stringify(payload)));
      setName("");
      setTeam("");
      reset();
      await refresh(seasonId);
    } catch (caught) {
      // The server replays a stored answer for a reused key, so a refused
      // request needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      setCreateError(messageFor(caught));
    } finally {
      setCreating(false);
    }
  }

  const stats = computeStats(state.blocks);
  const knownPillarIds = new Set(state.pillars.map((pillar) => pillar.id));
  const orphanBlocks = state.blocks.filter(
    (block) => block.pillar_id === null || !knownPillarIds.has(block.pillar_id)
  );

  return (
    <div className="blocks-view">
      <div className="form-row form-row--inline">
        <SeasonPicker seasons={seasons} value={seasonId} onChange={setSeasonId} />
        <button type="button" className="btn btn--ghost" onClick={() => router.navigate("/blocks/sizer")}>
          Open Block Sizer
        </button>
        {canConfigureEarning && (
          <button type="button" className="btn btn--ghost" onClick={() => router.navigate("/blocks/earning")}>
            Configure earning curve
          </button>
        )}
        {canManagePillars && (
          <button type="button" className="btn btn--ghost" onClick={() => router.navigate("/blocks/pillars")}>
            Manage pillars
          </button>
        )}
        {payoutStartPct !== null && (
          <span className="chip chip--impact">Blocks start earning at {payoutStartPct}%</span>
        )}
      </div>

      {canCreate && (
        <section className="card">
          <h2>New project</h2>
          <form onSubmit={handleCreate} className="form-row form-row--inline">
            <label>
              Name
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Connect"
                required
              />
            </label>
            <label>
              Pillar
              <select value={effectivePillarId} onChange={(event) => setPillarId(event.target.value)}>
                {activePillars.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Team
              <input type="text" value={team} onChange={(event) => setTeam(event.target.value)} placeholder="optional" />
            </label>
            <label>
              Due date
              <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} required />
            </label>
            <label>
              Impact
              <select value={impact} onChange={(event) => setImpact(Number(event.target.value))}>
                {scores.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Significance
              <select value={significance} onChange={(event) => setSignificance(Number(event.target.value))}>
                {scores.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={creating || name.trim() === "" || effectivePillarId === "" || dueDate === ""}
            >
              {creating ? "Creating…" : "Create project"}
            </button>
          </form>
          {createError !== null && <p className="form-row__error">{createError}</p>}
        </section>
      )}

      {state.status === "loading" && (
        <section className="card">
          <p className="loading">Loading…</p>
        </section>
      )}
      {state.status === "error" && state.errorMessage !== null && (
        <section className="card">
          <p className="form-row__error">{state.errorMessage}</p>
        </section>
      )}

      {state.status === "ready" && (
        <>
          <section className="stat-row">
            <StatCard label="Projects" value={stats.topCount} />
            <StatCard label="In Progress" value={stats.inProgress} />
            <StatCard label="Complete" value={stats.complete} />
            <StatCard label="Total Value" value={stats.total} />
          </section>

          {state.pillars.map((pillar) => {
            const inPillar = state.blocks
              .filter((block) => block.pillar_id === pillar.id)
              .sort((a, b) => b.size - a.size);
            // Archived pillars only render while blocks still reference them;
            // active pillars render even when empty — surfacing every company
            // area and its sponsor is the point of first-class pillars.
            if (pillar.archived && inPillar.length === 0) {
              return null;
            }
            const pillarTotal = inPillar.reduce((sum, block) => sum + block.size, 0);
            return (
              <section className="card pillar-section" key={pillar.id}>
                <div className="pillar-header">
                  <span
                    className="pillar-name pillar-name--tinted"
                    style={{ "--pillar-tint": pillarTint(pillar.id, pillar.name) } as React.CSSProperties}
                  >
                    {pillar.name}
                  </span>
                  {pillar.sponsor !== null && <UserRefChip user={pillar.sponsor} title="Sponsor" />}
                  {pillar.archived && <span className="chip">Archived</span>}
                  <span className="pillar-total">{pillarTotal} blocks</span>
                </div>
                {inPillar.length === 0 ? (
                  <p className="muted">No projects yet.</p>
                ) : (
                  <div className="block-card-grid">
                    {inPillar.map((block) => (
                      <BlockCard key={block.id} block={block} onOpen={(id) => router.navigate(`/blocks/${id}`)} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {/* Blocks whose pillar didn't come back (a restricted pillar the viewer
              can see blocks for would be a server bug, but never hide data). */}
          {orphanBlocks.length > 0 && (
            <section className="card pillar-section">
              <div className="pillar-header">
                <span className="pillar-name">No pillar</span>
                <span className="pillar-total">
                  {orphanBlocks.reduce((sum, block) => sum + block.size, 0)} blocks
                </span>
              </div>
              <div className="block-card-grid">
                {orphanBlocks.map((block) => (
                  <BlockCard key={block.id} block={block} onOpen={(id) => router.navigate(`/blocks/${id}`)} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat-card">
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
    </div>
  );
}

function computeStats(blocks: Block[]): {
  topCount: number;
  inProgress: number;
  complete: number;
  total: number;
} {
  // Headline stats are over the top-level blocks shown — the strategic portfolio
  // (the Phoenix total is computed the same way, plan §4.3).
  let inProgress = 0;
  let complete = 0;
  let total = 0;
  for (const block of blocks) {
    total += block.size;
    if (block.status === "in-progress") {
      inProgress += block.size;
    } else if (block.status === "complete") {
      complete += block.size;
    }
  }
  return { topCount: blocks.length, inProgress, complete, total };
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
